import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSidebar } from '@contexts/SidebarContext';
import { useAuth } from '@contexts/AuthContext';
import { useAsideContext } from '@contexts/AsideContext';
import { ArrowRightOnRectangleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { NAV_GROUPS, NavGroup } from '@/config/navigation';

const isNavItemActive = (pathname: string, to: string, end?: boolean) =>
  end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);

interface NavListProps {
  pathname: string;
  showLabels: boolean;
  onNavigate?: () => void;
}

const NavList: React.FC<NavListProps> = ({ pathname, showLabels, onNavigate }) => (
  <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
    {NAV_GROUPS.map((group: NavGroup, groupIndex) => (
      <div key={group.label ?? groupIndex} className="space-y-1">
        {group.label && showLabels && (
          <p className="px-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
            {group.label}
          </p>
        )}
        {group.items.map((item) => {
          const isActive = isNavItemActive(pathname, item.to, item.end);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                'text-sm font-medium',
                isActive
                  ? 'bg-brand-50 text-brand-600 dark:bg-brand-900/20 dark:text-brand-400'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              )}
              title={!showLabels ? item.label : undefined}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {showLabels && <span>{item.label}</span>}
            </Link>
          );
        })}
      </div>
    ))}
  </nav>
);

export const AppSidebar: React.FC = () => {
  const location = useLocation();
  const { admin, logout } = useAuth();
  const { isOpen: isAsideOpen } = useAsideContext();
  const {
    isExpanded,
    isMobileOpen,
    isHovered,
    toggleSidebar,
    toggleMobileSidebar,
    setIsHovered,
  } = useSidebar();

  const shouldExpand = (isExpanded || isHovered) && !isAsideOpen;
  const isVisible = isMobileOpen;

  return (
    <>
      {/* Desktop sidebar — collapses to 0 width while an Aside drawer is open, to give it room */}
      <aside
        className={clsx(
          'hidden md:flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700',
          'transition-all duration-300 ease-in-out',
          'fixed left-0 top-0 h-full z-40',
          isAsideOpen ? 'w-0 overflow-hidden border-r-0' : shouldExpand ? 'w-64' : 'w-20'
        )}
        onMouseEnter={() => !isExpanded && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-700 rounded-lg flex items-center justify-center text-white font-bold text-xl">
              C
            </div>
            {shouldExpand && (
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                Chicky
              </span>
            )}
          </Link>
        </div>

        <NavList pathname={location.pathname} showLabels={shouldExpand} />

        {/* Admin info & logout */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-4">
          {shouldExpand ? (
            <div className="space-y-3">
              <div className="px-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {admin?.firstName || admin?.username}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {admin?.role.toLowerCase().replace('_', ' ')}
                </p>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-3 w-full px-3 py-2 text-sm font-medium text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          ) : (
            <button
              onClick={logout}
              className="flex items-center justify-center w-full p-2 text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
              title="Logout"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Toggle button */}
        {!isAsideOpen && (
          <button
            onClick={toggleSidebar}
            className="absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors shadow-sm"
          >
            {isExpanded ? (
              <ChevronLeftIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
        )}
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={clsx(
          'md:hidden flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700',
          'fixed left-0 top-0 h-full w-64 z-40',
          'transform transition-transform duration-300 ease-in-out',
          isVisible ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-700 rounded-lg flex items-center justify-center text-white font-bold text-xl">
              C
            </div>
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              Chicky
            </span>
          </Link>
        </div>

        <NavList pathname={location.pathname} showLabels onNavigate={toggleMobileSidebar} />

        {/* Admin info & logout */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-4">
          <div className="space-y-3">
            <div className="px-3">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {admin?.firstName || admin?.username}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {admin?.role.toLowerCase().replace('_', ' ')}
              </p>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-3 w-full px-3 py-2 text-sm font-medium text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
